<template>
  <el-dialog
    :title="$t('machineUser.settings')"
    :visible.sync="localVisible"
    width="1200px"
    top="4vh"
    :append-to-body="true"
    :close-on-click-modal="false"
    @closed="onClosed"
    class="machine-settings-dialog"
  >
    <div class="ms-layout">
      <!-- Left nav -->
      <div class="ms-nav">
        <div
          v-if="canEdit"
          :class="['ms-nav-item', { active: tab === 'edit' }]"
          @click="activateTab('edit')"
        >
          <i class="el-icon-edit"></i>
          <span>{{ $t('common.edit') }}</span>
        </div>
        <div
          v-if="canPayment"
          :class="['ms-nav-item', { active: tab === 'payment' }]"
          @click="activateTab('payment')"
        >
          <i class="el-icon-bank-card"></i>
          <span>{{ $t('machineUser.payType') }}</span>
        </div>
        <div
          v-if="canLocation"
          :class="['ms-nav-item', { active: tab === 'location' }]"
          @click="activateTab('location')"
        >
          <i class="el-icon-location"></i>
          <span>{{ $t('machineUser.devicePosition') }}</span>
        </div>
        <div
          v-if="canConfig"
          :class="['ms-nav-item', { active: tab === 'config' }]"
          @click="activateTab('config')"
        >
          <i class="el-icon-s-tools"></i>
          <span>{{ $t('machineUser.mConfig') }}</span>
        </div>
      </div>

      <!-- Right content -->
      <div class="ms-content">
        <keep-alive>
          <edit-user-machine
            v-if="tab === 'edit' && mountedTabs.includes('edit')"
            key="edit"
            :embedded="true"
            :dialog-user-machine="true"
            :user-equipment="userEquipment"
            :machine-group="machineGroup"
            :yes-no="yesNo"
            :device-mode="deviceMode"
            @cancel="onCancel"
          />
          <payment
            v-else-if="tab === 'payment' && mountedTabs.includes('payment')"
            key="payment"
            :embedded="true"
            :dialog-payment="true"
            :payment="payment"
            :yes-no="yesNo"
            :shua-ka-qi="shuaKaQi"
            :coin-acceptor-item="coinAcceptorItem"
            :bill-acceptor-item="billAcceptorItem"
            @cancelPayment="onCancel"
          />
          <user-machine-map
            v-else-if="tab === 'location' && mountedTabs.includes('location')"
            key="location"
            :embedded="true"
            :dialog-machine-maps="true"
            :user-equipment="userEquipment"
            @mapsCancel="onCancel"
            @addressUpdated="$emit('addressUpdated', $event)"
          />
          <config-user-machine
            v-else-if="tab === 'config' && mountedTabs.includes('config')"
            key="config"
            :embedded="true"
            :dialog-config-machine="true"
            :user-equipment="configSubmit"
            :yes-no="yesNo"
            :age-device="ageDevice"
            :feel-lock="feelLock"
            :age-type="ageType"
            :priority-mode="priorityMode"
            :device-mode="deviceMode"
            :biaoshi="false"
            :idcard-country="idcardCountry"
            @cancel="onCancel"
          />
        </keep-alive>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import store from "@/store/store";
import EditUserMachine from "./EditUserMachine";
import Payment from "./Payment";
import UserMachineMap from "./UserMachineMap";
import ConfigUserMachine from "./ConfigUserMachine";

export default {
  name: "MachineSettings",
  components: { EditUserMachine, Payment, UserMachineMap, ConfigUserMachine },
  props: {
    visible: { type: Boolean, default: false },
    userEquipment: { type: Object, default: () => ({}) },
    configSubmit: { type: Object, default: () => ({}) },
    payment: { type: Object, default: () => ({}) },
    machineGroup: { type: Array, default: () => [] },
    yesNo: { type: Array, default: () => [] },
    deviceMode: { type: Array, default: () => [] },
    shuaKaQi: { type: Array, default: () => [] },
    coinAcceptorItem: { type: Array, default: () => [] },
    billAcceptorItem: { type: Array, default: () => [] },
    ageDevice: { type: Array, default: () => [] },
    feelLock: { type: Array, default: () => [] },
    ageType: { type: Array, default: () => [] },
    priorityMode: { type: Array, default: () => [] },
    idcardCountry: { type: Array, default: () => [] }
  },
  data() {
    return {
      tab: null,
      mountedTabs: []
    };
  },
  computed: {
    localVisible: {
      get() { return this.visible; },
      set(v) { this.$emit('update:visible', v); }
    },
    canEdit() { return this.hasPerm('user:machine:update'); },
    canPayment() { return this.hasPerm('user:machine:payment'); },
    canLocation() { return this.hasPerm('user:equipment:position'); },
    canConfig() { return this.hasPerm('user:equipment:config'); }
  },
  watch: {
    visible(v) {
      if (v) this.initTab();
    }
  },
  methods: {
    hasPerm(perm) {
      const permissions = store.state.permissions || [];
      return permissions.some(p => p === '*:*:*' || p === perm);
    },
    initTab() {
      this.mountedTabs = [];
      const first = ['edit', 'payment', 'location', 'config'].find(t => this[`can${t.charAt(0).toUpperCase() + t.slice(1)}`]);
      this.activateTab(first || 'edit');
    },
    activateTab(name) {
      this.tab = name;
      if (!this.mountedTabs.includes(name)) {
        this.mountedTabs.push(name);
      }
    },
    onCancel(dialog, state) {
      if (state) this.$emit('refresh');
      this.localVisible = false;
    },
    onClosed() {
      this.tab = null;
      this.mountedTabs = [];
    }
  }
};
</script>

<style scoped>
.ms-layout {
  display: flex;
  height: 70vh;
  gap: 0;
}
.ms-nav {
  width: 160px;
  border-right: 1px solid var(--vtm-line);
  flex-shrink: 0;
  padding: 8px 0;
}
.ms-nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  cursor: pointer;
  font-size: 13px;
  color: var(--vtm-ink-2);
  border-radius: 0 4px 4px 0;
  transition: all 0.15s;
  margin-right: 4px;
  word-break: break-word;
}
.ms-nav-item:hover {
  background: var(--vtm-surface-2);
  color: var(--vtm-ink);
}
.ms-nav-item.active {
  background: var(--vtm-mint-soft);
  color: var(--vtm-mint-ink);
  font-weight: 600;
  border-right: 3px solid var(--vtm-mint);
}
.ms-nav-item i {
  font-size: 15px;
}
.ms-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
}
</style>

<style>
.machine-settings-dialog .el-dialog__body {
  padding: 0 !important;
}
</style>
